// monitorService.js
// -----------------------------------------------------------------------------
// Every Firestore access for กระดานติดตามรถ. The view must never touch `db`
// directly -- the same contract driverService.js, bookingService.js and
// dispatchService.js keep.
//
// All three queries are `in` on deliveryDate alone (today plus the departure day
// of each running trip), which Firestore serves from its automatic single-field
// indexes. NO COMPOSITE INDEX IS NEEDED, and
// none must be added: firestore.indexes.json has never been a complete record of
// this project's indexes and deploying it offers to delete the ones that exist.
//
// The judgement -- what late means, what stale means, what order trucks appear
// in -- is in fleetBoard.js, which is pure and covered by
// `npm run test:monitor`. What lives here is the three listeners, the tick that
// re-runs that judgement while nothing is being written, their teardown, and
// converting Firestore Timestamps to JS Dates so the pure module never sees a
// Firestore type.
// -----------------------------------------------------------------------------

import { db } from '@/firebase'
import moment from 'moment-timezone'
import F from '@/components/fleetBoard'
import tripLookup from '@/components/tripLookup'
import { reportWarning } from '@/components/report'

const TZ = 'Asia/Bangkok'

// How often the board is recomputed from data already in hand, with no
// Firestore read at all.
//
// A snapshot-driven board freezes between writes, and the failure it hides is
// the exact one it exists to show: the driver's phone locks, no ping is
// written, no snapshot arrives, and "อัปเดต 2 นาทีที่แล้ว" stays on screen for
// an hour -- so a fix can never age into `stale` and the
// โทรศัพท์อาจล็อกหน้าจอ caveat is unreachable. Lateness grows the same way,
// against a clock nothing was reading.
//
// A minute is finer than the ten-minute ping floor, so no age band is ever
// missed, and it costs nothing: the tick re-reads nothing.
const TICK_MS = 60 * 1000

function todayISO () {
  return moment.tz(TZ).format('YYYY-MM-DD')
}

// A Firestore Timestamp, a Date, or nothing -> a Date or null. serverTimestamp()
// reads back as null on the writer's own optimistic local snapshot before the
// server confirms, so null here is normal and must not throw.
function toDate (value) {
  if (!value) return null
  if (value instanceof Date) return value
  if (typeof value.toDate === 'function') return value.toDate()
  return null
}

/**
 * Watches one day and calls `onBoard` with a fresh fleetBoard array whenever any
 * of the three collections changes.
 *
 * Returns an unsubscribe function. THE CALLER MUST CALL IT -- in beforeDestroy
 * and again whenever the date changes. A board left open across date changes
 * would otherwise accumulate listeners silently for the life of the tab.
 */
function watchDay (date, onBoard, onError) {
  const day = date || todayISO()
  let stopped = false
  let inner = null
  const start = (tripKeys) => {
    if (stopped) return
    // Today plus each running trip's departure day (`in` caps at 10).
    const dates = Array.from(new Set([day].concat(tripKeys.map(k => k.date)))).slice(0, 10)
    inner = listen(day, dates, tripKeys, onBoard, onError)
  }
  if (day === todayISO()) {
    tripLookup.readOpenTrips(day, { overdueDays: 3 }).then(start, e => {
      if (stopped) return
      // Degrades quietly to today only: the board still works without trips.
      reportWarning('monitor: trip lookup failed; showing today only', e)
      start([])
    })
  } else start([])
  return function unsubscribe () { stopped = true; if (inner) inner() }
}

function listen (day, dates, tripKeys, onBoard, onError) {
  let rows = []
  let stopDocs = {}
  let locationDocs = {}
  let ready = { rows: false, stops: false, locations: false }

  const emit = () => {
    // Nothing is emitted until all three have answered once, so the board never
    // paints a truck as "not started" merely because the stop documents have not
    // arrived yet.
    if (!ready.rows || !ready.stops || !ready.locations) return
    onBoard(F.buildBoard(rows, stopDocs, locationDocs, new Date(), { day, tripKeys }))
  }

  // A listener that fails marks ITSELF ready, with no data, and still reports
  // the error. Otherwise one broken query holds the readiness gate shut and a
  // board whose rows and stops both arrived shows nothing at all.
  //
  // driverLocation is why this matters: it is the newest collection and the
  // only narrowed one, so it is the one a rules edit will deny first -- and the
  // positions are the OPTIONAL half of this screen. Losing them must cost the
  // dots on the map, not the board.
  const failWith = key => e => {
    ready[key] = true
    if (onError) onError(e)
    emit()
  }

  const unsubRows = db.collection('deliveryList')
    .where('deliveryDate', 'in', dates)
    .onSnapshot(snap => {
      const next = []
      snap.forEach(doc => next.push(Object.assign({ id: doc.id }, doc.data())))
      rows = next
      ready.rows = true
      emit()
    }, failWith('rows'))

  const unsubStops = db.collection('deliveryStop')
    .where('deliveryDate', 'in', dates)
    .onSnapshot(snap => {
      const next = {}
      snap.forEach(doc => {
        const d = doc.data() || {}
        const steps = {}
        Object.keys(d.steps || {}).forEach(key => {
          const step = d.steps[key] || {}
          steps[key] = Object.assign({}, step, { at: toDate(step.at) })
        })
        next[doc.id] = Object.assign({}, d, { steps: steps })
      })
      stopDocs = next
      ready.stops = true
      emit()
    }, failWith('stops'))

  const unsubLocations = db.collection('driverLocation')
    .where('deliveryDate', 'in', dates)
    .onSnapshot(snap => {
      const next = {}
      snap.forEach(doc => {
        const d = doc.data() || {}
        next[doc.id] = Object.assign({}, d, { at: toDate(d.at) })
      })
      locationDocs = next
      ready.locations = true
      emit()
    }, failWith('locations'))

  // Recompute on the clock as well as on writes. See TICK_MS.
  const timer = setInterval(emit, TICK_MS)

  return function unsubscribe () {
    clearInterval(timer)
    unsubRows()
    unsubStops()
    unsubLocations()
  }
}

export default {
  watchDay,
  todayISO
}
